/**
 * Dates et periodes. Fonctions pures, aucune dependance.
 *
 * ## Deux conventions
 *
 * - une **date comptable** est un jour calendaire local, `YYYY-MM-DD`. Elle n'a
 *   pas d'heure : une facture est du 16 septembre, pas du 16 septembre a 14 h 32.
 * - un **mois** est `YYYY-MM`. Dans les deux cas l'ordre lexicographique des
 *   chaines coincide avec l'ordre chronologique, ce dont dependent les index
 *   Dexie et tous les tris.
 *
 * ## Pourquoi midi et jamais minuit
 *
 * Les `Date` intermediaires sont construites a **12 h 00** locales. Une date
 * posee a minuit tombe sur l'heure exacte ou certains fuseaux avancent leur
 * horloge : ce minuit-la n'existe pas, et le moteur le resout en reculant ou
 * avancant d'une heure — ce qui, sur un `getDate()`, peut changer le jour. A
 * midi, aucun decalage saisonnier connu (au plus une heure) ne peut faire
 * franchir une frontiere de jour.
 */

import type { DayKey, Instant, MoisKey } from './types'

export function nowInstant(): Instant {
  return new Date().toISOString()
}

// ---------------------------------------------------------------------------
// Conversions
// ---------------------------------------------------------------------------

const p2 = (n: number) => String(n).padStart(2, '0')

export function dayKeyOf(d: Date): DayKey {
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`
}

export function aujourdhui(): DayKey {
  return dayKeyOf(new Date())
}

/**
 * `Date` locale a midi correspondant a une cle de jour.
 *
 * Surtout pas `new Date('2026-09-16')` : la norme impose d'interpreter cette
 * forme en **UTC**, donc a minuit UTC, ce qui recule d'un jour partout a
 * l'ouest de Greenwich — et la Suisse est a l'est, ce qui rend le bug
 * indetectable en developpement local mais bien reel a l'usage.
 */
export function dateDeDayKey(key: DayKey): Date {
  const [y, m, j] = key.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, j ?? 1, 12, 0, 0, 0)
}

export function dateDeMoisKey(mois: MoisKey): Date {
  const [y, m] = mois.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, 1, 12, 0, 0, 0)
}

export function moisDe(key: DayKey): MoisKey {
  return key.slice(0, 7)
}

export function anneeDe(key: DayKey | MoisKey): number {
  return Number(key.slice(0, 4))
}

export function moisCourant(): MoisKey {
  return moisDe(aujourdhui())
}

export function estDayKey(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(v)
}

// ---------------------------------------------------------------------------
// Arithmetique de calendrier
// ---------------------------------------------------------------------------

export function ajouterJours(key: DayKey, n: number): DayKey {
  const d = dateDeDayKey(key)
  d.setDate(d.getDate() + n)
  return dayKeyOf(d)
}

export function ajouterMoisKey(mois: MoisKey, n: number): MoisKey {
  const d = dateDeMoisKey(mois)
  d.setMonth(d.getMonth() + n)
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}`
}

/** Nombre de jours du mois (1-12 pour `mois`). */
export function joursDansMois(annee: number, mois: number): number {
  return new Date(annee, mois, 0).getDate()
}

/**
 * Decale une date de `n` mois **en gardant le jour d'ancrage**.
 *
 * C'est le coeur des echeances recurrentes, et le piege classique. Un
 * prelevement au 31 doit tomber :
 *
 *   31.01 → 28.02 → 31.03 → 30.04 → 31.05 …
 *
 * L'implementation naive (`setMonth(+1)` sur la date precedente) donne
 * `31.01 → 03.03` (debordement), et sa correction naive (rabattre puis repartir
 * de la date rabattue) donne `31.01 → 28.02 → 28.03` : le 31 est perdu pour
 * toujours des le premier mois court.
 *
 * On recalcule donc **toujours depuis l'ancrage d'origine**, en rabattant
 * seulement au moment de l'affichage de l'echeance concernee.
 */
export function ajouterMoisAncre(ancrage: DayKey, n: number): DayKey {
  const [y, m, j] = ancrage.split('-').map(Number)
  const annee = y ?? 1970
  const mois = (m ?? 1) - 1
  const jour = j ?? 1

  const cible = new Date(annee, mois + n, 1, 12, 0, 0, 0)
  const max = joursDansMois(cible.getFullYear(), cible.getMonth() + 1)
  cible.setDate(Math.min(jour, max))
  return dayKeyOf(cible)
}

export function premierJourDuMois(mois: MoisKey): DayKey {
  return `${mois}-01`
}

export function dernierJourDuMois(mois: MoisKey): DayKey {
  const [y, m] = mois.split('-').map(Number)
  return `${mois}-${p2(joursDansMois(y ?? 1970, m ?? 1))}`
}

/** Tous les mois de `debut` a `fin`, bornes comprises. */
export function moisEntre(debut: MoisKey, fin: MoisKey): MoisKey[] {
  if (debut > fin) return []
  const out: MoisKey[] = []
  let m = debut
  // Garde-fou : une plage aberrante (mois mal forme) ne doit pas boucler sans fin.
  for (let i = 0; i < 1200 && m <= fin; i++) {
    out.push(m)
    m = ajouterMoisKey(m, 1)
  }
  return out
}

export function moisDeLAnnee(annee: number): MoisKey[] {
  return Array.from({ length: 12 }, (_, i) => `${annee}-${p2(i + 1)}`)
}

/** Les `n` mois qui precedent `mois`, du plus ancien au plus recent, `mois` exclu. */
export function moisPrecedents(mois: MoisKey, n: number): MoisKey[] {
  return Array.from({ length: n }, (_, i) => ajouterMoisKey(mois, i - n))
}

export function estDansMois(key: DayKey, mois: MoisKey): boolean {
  return key.startsWith(mois)
}

export function estDansAnnee(key: DayKey, annee: number): boolean {
  return key.startsWith(String(annee))
}

// ---------------------------------------------------------------------------
// Mise en forme
// ---------------------------------------------------------------------------

// Suisse : le jour se note `16.09.2026`, pas `16/09/2026`.
const FMT_JOUR = new Intl.DateTimeFormat('fr-CH', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

const FMT_JOUR_LONG = new Intl.DateTimeFormat('fr-CH', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const FMT_JOUR_COURT = new Intl.DateTimeFormat('fr-CH', {
  day: '2-digit',
  month: '2-digit',
})

const FMT_MOIS = new Intl.DateTimeFormat('fr-CH', { month: 'long', year: 'numeric' })
const FMT_MOIS_COURT = new Intl.DateTimeFormat('fr-CH', { month: 'short' })

export function formatJour(key: DayKey): string {
  return FMT_JOUR.format(dateDeDayKey(key))
}

export function formatJourLong(key: DayKey): string {
  return FMT_JOUR_LONG.format(dateDeDayKey(key))
}

export function formatJourCourt(key: DayKey): string {
  return FMT_JOUR_COURT.format(dateDeDayKey(key))
}

export function formatMois(mois: MoisKey): string {
  return FMT_MOIS.format(dateDeMoisKey(mois))
}

export function formatMoisCourt(mois: MoisKey): string {
  return FMT_MOIS_COURT.format(dateDeMoisKey(mois))
}

/** Majuscule initiale : « Septembre 2026 » en tete de page. */
export function capitaliser(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
